import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Search, Plus, ArrowRight, CheckCircle2, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/api";
import { Input } from "@/components/ui/input";
import { ProviderLogo } from "@/components/ProviderCard";
import Seo from "@/components/Seo";

export default function ListYourCompany() {
  const [mode, setMode] = useState(null); // "claim" | "submit" | null
  const [params] = useSearchParams();
  const showUnlinkedBanner = params.get("unlinked") === "1";
  return (
    <div className="min-h-screen bg-slate-50">
      <Seo
        title="Claim or Submit your Company Listing"
        description="List your tracking company on Strategic Tracking. Claim an existing profile or submit a new company for review. Basic listings are free. Verified ($197/mo) and Featured ($297/mo) are available after manual review."
      />

      {showUnlinkedBanner && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-5xl mx-auto px-4 py-4 flex items-start gap-3">
            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={18}/>
            <div className="text-sm text-amber-900">
              <b>Your account is not linked to an approved provider listing yet.</b>
              <div className="text-amber-800 mt-0.5">Claim an existing listing or submit a new company. Once approved, you’ll be able to access your provider dashboard.</div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 pt-16 pb-10">
        <div className="text-xs uppercase tracking-widest text-[#002FA7] font-semibold">For providers</div>
        <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight text-slate-900 mt-2">
          Claim or submit your company listing
        </h1>
        <p className="mt-4 text-slate-600 max-w-3xl leading-relaxed">
          Strategic Tracking helps buyers find and compare tracking device manufacturers, visibility platforms, cold chain monitoring providers, smart label companies and asset tracking solutions globally.
        </p>
        <p className="mt-3 text-slate-600 max-w-3xl leading-relaxed">
          Search for your company to claim an existing listing, or submit a new company for review. Basic listings are free. Verified and Featured options are available for providers who want enhanced visibility and lead-generation features.
        </p>
      </div>

      {mode === null && <OptionCards onChoose={setMode} />}
      {mode === "claim" && <ClaimFlow onBack={() => setMode(null)} />}
      {mode === "submit" && <SubmitFlow onBack={() => setMode(null)} />}
    </div>
  );
}

/* ---------------- Option cards ---------------- */

function OptionCards({ onChoose }) {
  return (
    <div className="max-w-5xl mx-auto px-4 pb-24 grid md:grid-cols-2 gap-5">
      <button
        type="button"
        onClick={() => onChoose("claim")}
        className="card-elevated p-8 text-left transition-all hover:shadow-lg hover:-translate-y-0.5 border-2 border-transparent hover:border-[#002FA7]"
        data-testid="option-claim"
      >
        <div className="w-12 h-12 rounded-md bg-slate-900 text-white grid place-items-center"><Search size={20}/></div>
        <div className="mt-5 font-display font-extrabold text-2xl text-slate-900">Claim existing listing</div>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Already listed? Claim your company profile and request access to manage your basic listing.
        </p>
        <div className="mt-6 text-sm font-semibold text-[#002FA7] inline-flex items-center gap-2">
          Search for my company <ArrowRight size={14}/>
        </div>
      </button>

      <button
        type="button"
        onClick={() => onChoose("submit")}
        className="card-elevated p-8 text-left transition-all hover:shadow-lg hover:-translate-y-0.5 border-2 border-transparent hover:border-emerald-500"
        data-testid="option-submit"
      >
        <div className="w-12 h-12 rounded-md bg-emerald-600 text-white grid place-items-center"><Plus size={20}/></div>
        <div className="mt-5 font-display font-extrabold text-2xl text-slate-900">Submit a new listing</div>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Not listed yet? Submit your company for review and inclusion in the Strategic Tracking Solution Directory.
        </p>
        <div className="mt-6 text-sm font-semibold text-emerald-700 inline-flex items-center gap-2">
          Submit new listing <ArrowRight size={14}/>
        </div>
      </button>

      <div className="md:col-span-2 mt-2 text-center text-xs text-slate-500">
        All listings are reviewed and activated manually by Strategic Tracking. Claim approval does not automatically activate Verified, Featured or direct lead routing.
      </div>
    </div>
  );
}

/* ---------------- Claim flow (search-driven) ---------------- */

function ClaimFlow({ onBack }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setItems([]);
      setTouched(false);
      return;
    }
    setTouched(true);
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/providers?q=${encodeURIComponent(q.trim())}&page_size=10`);
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="max-w-5xl mx-auto px-4 pb-24">
      <BackBtn onBack={onBack}/>
      <div className="card-elevated p-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-slate-900 text-white grid place-items-center"><Search size={18}/></div>
          <div>
            <div className="font-display font-extrabold text-2xl text-slate-900">Find your company</div>
            <p className="text-sm text-slate-500">Search by company name, website domain, or country.</p>
          </div>
        </div>

        <div className="mt-6 relative">
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search for your company name"
            className="h-12 text-base pl-11"
            data-testid="claim-search-input"
          />
          <Search className="absolute left-3 top-3.5 text-slate-400" size={18}/>
        </div>

        <div className="mt-6 space-y-3" data-testid="claim-results">
          {loading && <div className="text-sm text-slate-400">Searching…</div>}
          {!loading && touched && items.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 p-6 text-center bg-white">
              <Building2 className="mx-auto text-slate-300" size={36}/>
              <div className="mt-3 font-semibold text-slate-700">No matching company found</div>
              <p className="text-sm text-slate-500 mt-1">Couldn’t find your company in the directory?</p>
              <Link to="#" onClick={onBack} className="hidden"/>
              <button
                onClick={onBack}
                type="button"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline"
                data-testid="claim-fallback-submit"
              >
                Submit a new listing instead <ArrowRight size={14}/>
              </button>
            </div>
          )}
          {!loading && items.map((p) => <ResultRow key={p.id} provider={p} onClaim={(slug) => navigate(`/claim-listing/${slug}`)}/>)}
        </div>
      </div>
    </div>
  );
}

function ResultRow({ provider, onClaim }) {
  const claimed = provider.claim_status === "Claimed";
  return (
    <div className="card-elevated p-5 flex items-center gap-4" data-testid={`claim-result-${provider.slug}`}>
      <ProviderLogo provider={provider} size={56}/>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-slate-900 truncate">{provider.name}</div>
        <div className="text-xs text-slate-500 truncate">
          {[provider.hq_country, provider.website].filter(Boolean).join(" · ")}
        </div>
        {provider.tagline && <div className="text-xs text-slate-500 mt-1 truncate">{provider.tagline}</div>}
      </div>
      <div className="shrink-0">
        {claimed ? (
          <div className="text-right">
            <span className="tag text-xs bg-slate-100 text-slate-600 border-slate-200">Already claimed</span>
            <div className="text-[11px] text-slate-400 mt-1">Contact us if this seems wrong.</div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onClaim(provider.slug)}
            className="btn-primary text-sm"
            data-testid={`claim-btn-${provider.slug}`}
          >
            Claim this listing
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------- Submit flow (handoff to existing page) ---------------- */

function SubmitFlow({ onBack }) {
  return (
    <div className="max-w-3xl mx-auto px-4 pb-24">
      <BackBtn onBack={onBack}/>
      <div className="card-elevated p-8 text-center">
        <div className="w-12 h-12 rounded-md bg-emerald-600 text-white grid place-items-center mx-auto"><Plus size={20}/></div>
        <div className="mt-4 font-display font-extrabold text-2xl text-slate-900">Submit a new company</div>
        <p className="mt-3 text-sm text-slate-600 max-w-xl mx-auto leading-relaxed">
          New listings are reviewed by Strategic Tracking before being published. Default status:
          <strong className="text-slate-900"> Basic, Pending admin review</strong>. Verified and Featured placements are
          available after manual review.
        </p>
        <Link to="/submit-provider" className="btn-primary mt-6 inline-flex" data-testid="submit-continue">
          Continue to submission form <ArrowRight size={14}/>
        </Link>

        <div className="mt-8 grid sm:grid-cols-2 gap-3 text-left">
          <Reassurance icon={<ShieldCheck size={16}/>} title="Basic listings are free" body="No payment is taken at submission. Listings start as Basic and unclaimed."/>
          <Reassurance icon={<CheckCircle2 size={16}/>} title="Manual review keeps quality high" body="Our team checks every new submission before publishing. Most decisions take 1–2 business days."/>
        </div>
      </div>
    </div>
  );
}

function Reassurance({ icon, title, body }) {
  return (
    <div className="rounded-md border border-slate-200 p-4 bg-white">
      <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm">
        <span className="w-7 h-7 rounded bg-slate-900 text-white grid place-items-center">{icon}</span>
        {title}
      </div>
      <p className="text-xs text-slate-500 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function BackBtn({ onBack }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="text-sm text-slate-500 hover:text-slate-900 mb-4 inline-flex items-center gap-2"
      data-testid="back-to-options"
    >
      ← Back to options
    </button>
  );
}
